import { NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'

import Meta from 'lib/meta'

import ApplicationSection from 'components/domains/partnerIntroduction/ApplicationSection'
import BillboardSection from 'components/domains/partnerIntroduction/BillboardSection'
import ClosingSection from 'components/domains/partnerIntroduction/ClosingSection'
import ConditionsSection from 'components/domains/partnerIntroduction/ConditionsSection'
import FaqSection from 'components/domains/partnerIntroduction/FaqSection'
import Footer from 'components/domains/partnerIntroduction/Footer'
import Header from 'components/domains/partnerIntroduction/Header'
import HeroSection from 'components/domains/partnerIntroduction/HeroSection'
import IntroductionFlowSection from 'components/domains/partnerIntroduction/IntroductionFlowSection'
import MapSection from 'components/domains/partnerIntroduction/MapSection'
import ProblemSection from 'components/domains/partnerIntroduction/ProblemSection'
import ServiceDocumentSection from 'components/domains/partnerIntroduction/ServiceDocumentSection'
import ServiceSection from 'components/domains/partnerIntroduction/ServiceSection'
import StickyNavigation from 'components/domains/partnerIntroduction/StickyNavigation'
import TargetStoreSection from 'components/domains/partnerIntroduction/TargetStoreSection'

import styles from './styles.module.scss'

type Props = {
  locale: string
}

const PartnerIntroduction: NextPage<Props> = (props) => {
  const { asPath } = useRouter()
  const pageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth'
    const previousHtmlScrollBehavior = document.documentElement.style.scrollBehavior
    const previousBodyScrollBehavior = document.body.style.scrollBehavior

    document.documentElement.style.scrollBehavior = behavior
    document.body.style.scrollBehavior = behavior

    return () => {
      document.documentElement.style.scrollBehavior = previousHtmlScrollBehavior
      document.body.style.scrollBehavior = previousBodyScrollBehavior
    }
  }, [])

  useEffect(() => {
    const root = pageRef.current
    if (!root) return
    const elements = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'))
    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      typeof IntersectionObserver === 'undefined'
    ) {
      elements.forEach((element) => {
        element.setAttribute('data-reveal', 'visible')
      })
      return
    }

    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.setAttribute('data-reveal', 'visible')
          observer.unobserve(entry.target)
        }),
      { threshold: 0.12 }
    )
    elements.forEach((element) => observer.observe(element))
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <Meta
        title="日本全国 荷物預かりマップを埋めよう | ecbo cloak 紹介キャンペーン"
        description="あなたの紹介で、荷物を預けられる場所を全国に。ecbo cloakの店舗・施設紹介キャンペーンです。"
        url={`/${props.locale === 'default' ? 'ja' : props.locale}${asPath}`}
      />
      <div className={styles.page} ref={pageRef}>
        <Header />
        <main>
          <HeroSection />
          <BillboardSection />
          <ProblemSection />
          <ServiceSection />
          <MapSection />
          <TargetStoreSection />
          <ConditionsSection />
          <IntroductionFlowSection />
          <ApplicationSection />
          <FaqSection />
          <ServiceDocumentSection />
          <ClosingSection />
        </main>
        <Footer />
        <StickyNavigation />
      </div>
    </>
  )
}

export async function getStaticProps({ locale }: Props) {
  return {
    props: {
      locale,
      ...(await serverSideTranslations(locale, [])),
    },
  }
}

export default PartnerIntroduction
